import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Service, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, finalize } from 'rxjs';
import { SolicitacaoServicoPublicaService } from './solicitacao-servico-publica.service';

/** Uma casa da grade do calendário. `dia` nulo é a casa vazia antes do dia 1. */
export interface DiaCalendario {
  dia: number | null;
  iso: string;
  hoje: boolean;
  indisponivel: boolean;
}

export interface MesReferencia {
  ano: number;
  /** 0 a 11. */
  mes: number;
}

/** De qual agenda o calendário lê: a de um serviço, num dos postos dele. */
interface FonteAgenda {
  codServicoSrv: number;
  /** Nulo é o endereço principal do serviço. */
  codLocalAtendimentoServicoLas: number | null;
}

const MESES = [
  'JANEIRO',
  'FEVEREIRO',
  'MARÇO',
  'ABRIL',
  'MAIO',
  'JUNHO',
  'JULHO',
  'AGOSTO',
  'SETEMBRO',
  'OUTUBRO',
  'NOVEMBRO',
  'DEZEMBRO',
];

/** As iniciais do cabeçalho da grade, de domingo a sábado. */
export const DIAS_SEMANA: readonly string[] = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

function paraIso(ano: number, mes: number, dia: number): string {
  return `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

function paraMes(iso: string): MesReferencia {
  const [ano, mes] = iso.split('-').map(Number);

  return { ano, mes: mes - 1 };
}

function chaveMes({ ano, mes }: MesReferencia): string {
  return `${ano}-${String(mes + 1).padStart(2, '0')}`;
}

/** Só para a primeira carga, antes de a API dizer qual é o primeiro dia. */
function mesDoRelogio(): MesReferencia {
  const hoje = new Date();

  return { ano: hoje.getFullYear(), mes: hoje.getMonth() };
}

/**
 * O calendário mensal da **agenda de um serviço num posto**: o mês exibido, os dias com horário
 * livre e a navegação. É a lógica que vivia dentro do modal "Agendar data e hora".
 *
 * Os horários não moram no portal: cada mês exibido é **uma** chamada a
 * `publico/servico/{id}/agenda?mes=aaaa-mm[&posto=]`, que devolve só os dias com horário livre.
 * Dia fora da lista fica indisponível — lotado, sem atendimento ou além do horizonte, a tela não
 * precisa saber qual. A navegação vai só de `dataMinima` a `dataMaxima`, que vêm da API.
 *
 * O que a tela mostra é uma fotografia: a vaga só é garantida no envio, e um horário tomado
 * nesse meio-tempo volta como 409.
 *
 * Provido por quem o usa (`providers`), e não na raiz: o modal da página do serviço e o wizard
 * da área do cidadão têm cada um o seu calendário, com marcação e CSS próprios. A escolha do
 * dia e do horário é de cada tela — aqui fica só o que a agenda oferece.
 */
@Service({ autoProvided: false })
export class AgendaMensalStore {
  private readonly api = inject(SolicitacaoServicoPublicaService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly fonte = signal<FonteAgenda | null>(null);

  private readonly mes = signal<MesReferencia>(mesDoRelogio());
  private readonly horarios = signal<ReadonlyMap<string, readonly string[]>>(new Map());
  private readonly intervalo = signal<{ minima: string; maxima: string } | null>(null);
  private readonly noAr = signal(false);
  private readonly comFalha = signal(false);
  private readonly semAgenda = signal(false);

  private assinatura?: Subscription;

  /** O mês exibido. */
  readonly mesReferencia = this.mes.asReadonly();

  /** Os horários livres do mês exibido, por data `aaaa-mm-dd`. */
  readonly horariosPorDia = this.horarios.asReadonly();

  /** O intervalo que a agenda oferece — vem da API, não do relógio do navegador. */
  readonly limites = this.intervalo.asReadonly();

  readonly carregando = this.noAr.asReadonly();
  readonly falhou = this.comFalha.asReadonly();

  /**
   * A falha foi um `404`: a agenda pedida não existe mais — o posto deixou de receber
   * agendamento, ou o serviço saiu do portal. Vem sempre junto de `falhou`; quem não distingue
   * os dois casos continua mostrando só o "Tentar novamente".
   */
  readonly naoEncontrada = this.semAgenda.asReadonly();

  readonly rotuloMes = computed(() => {
    const { ano, mes } = this.mes();

    return `${MESES[mes]} ${ano}`;
  });

  readonly diasDoMes = computed<readonly DiaCalendario[]>(() => {
    const { ano, mes } = this.mes();
    const primeiroDiaSemana = new Date(ano, mes, 1).getDay();
    const totalDias = new Date(ano, mes + 1, 0).getDate();
    const livres = this.horarios();
    const hoje = this.intervalo()?.minima ?? '';

    // As casas antes do primeiro dia mantêm o alinhamento das colunas da semana.
    const vazios: DiaCalendario[] = Array.from({ length: primeiroDiaSemana }, (_, i) => ({
      dia: null,
      iso: `vazio-${i}`,
      hoje: false,
      indisponivel: true,
    }));

    const dias: DiaCalendario[] = Array.from({ length: totalDias }, (_, i) => {
      const iso = paraIso(ano, mes, i + 1);

      return { dia: i + 1, iso, hoje: iso === hoje, indisponivel: !livres.has(iso) };
    });

    return [...vazios, ...dias];
  });

  readonly podeVoltarMes = computed(() => {
    const limites = this.intervalo();

    return !limites || chaveMes(this.mes()) > limites.minima.slice(0, 7);
  });

  readonly podeAvancarMes = computed(() => {
    const limites = this.intervalo();

    return !limites || chaveMes(this.mes()) < limites.maxima.slice(0, 7);
  });

  readonly mesSemHorarios = computed(() => !this.noAr() && !this.comFalha() && this.horarios().size === 0);

  /**
   * Aponta o calendário para a agenda de um serviço num posto — sem `codLocal`, o endereço
   * principal. `codServicoSrv` nulo é "nenhuma agenda": nada é buscado.
   *
   * Fonte diferente da atual esquece tudo o que foi lido (dias, limites, falha) e volta ao mês
   * do relógio: a outra agenda tem outro horizonte e outra ocupação. A mesma fonte não mexe em
   * nada.
   *
   * ⚠️ Não busca: devolve se a fonte mudou, e quem chama decide quando e em que mês carregar. O
   * modal carrega a cada abertura, no mês da escolha anterior; o wizard, quando a fonte muda.
   * Buscar aqui faria o modal pedir dois meses na mesma abertura.
   */
  definirFonte(codServicoSrv: number | null, codLocalAtendimentoServicoLas: number | null = null): boolean {
    const atual = this.fonte();
    const nova = codServicoSrv === null ? null : { codServicoSrv, codLocalAtendimentoServicoLas };

    const mesma =
      atual?.codServicoSrv === nova?.codServicoSrv &&
      atual?.codLocalAtendimentoServicoLas === nova?.codLocalAtendimentoServicoLas;

    if (mesma) return false;

    // A resposta que ainda estiver no ar é da agenda antiga.
    this.assinatura?.unsubscribe();

    this.fonte.set(nova);
    this.intervalo.set(null);
    this.horarios.set(new Map());
    this.comFalha.set(false);
    this.semAgenda.set(false);
    this.mes.set(mesDoRelogio());

    return true;
  }

  /** Os horários livres de uma data do mês exibido, em ordem; vazio para qualquer outra. */
  horariosDe(dataIso: string): readonly string[] {
    return this.horarios().get(dataIso) ?? [];
  }

  /**
   * Busca o mês exibido. Cada chamada cancela a anterior: vale a resposta do último mês pedido.
   */
  carregarMes(): void {
    const fonte = this.fonte();

    if (fonte === null) return;

    this.assinatura?.unsubscribe();
    this.noAr.set(true);
    this.comFalha.set(false);
    this.semAgenda.set(false);
    this.horarios.set(new Map());

    const pedido = chaveMes(this.mes());

    this.assinatura = this.api
      .agenda(fonte.codServicoSrv, pedido, fonte.codLocalAtendimentoServicoLas)
      .pipe(
        finalize(() => this.noAr.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (agenda) => {
          this.intervalo.set({ minima: agenda.dataMinima, maxima: agenda.dataMaxima });
          this.horarios.set(new Map(agenda.dias.map((dia) => [dia.data, dia.horarios])));

          // Primeira carga com o relógio do navegador num mês anterior ao da agenda: pula para
          // o primeiro mês que ela oferece.
          if (pedido < agenda.dataMinima.slice(0, 7)) {
            this.mes.set(paraMes(agenda.dataMinima));
            this.carregarMes();
          }
        },
        error: (erro: unknown) => {
          this.comFalha.set(true);
          this.semAgenda.set(erro instanceof HttpErrorResponse && erro.status === 404);
        },
      });
  }

  mesAnterior(): void {
    if (!this.podeVoltarMes()) return;

    this.mes.update(({ ano, mes }) => (mes === 0 ? { ano: ano - 1, mes: 11 } : { ano, mes: mes - 1 }));
    this.carregarMes();
  }

  proximoMes(): void {
    if (!this.podeAvancarMes()) return;

    this.mes.update(({ ano, mes }) => (mes === 11 ? { ano: ano + 1, mes: 0 } : { ano, mes: mes + 1 }));
    this.carregarMes();
  }

  /** "Tentar novamente" da falha — e a releitura do mês depois de um horário tomado. */
  recarregar(): void {
    this.carregarMes();
  }

  /** Mostra e busca o mês de uma data (`aaaa-mm-dd`) — o da escolha que a tela já tem. */
  irParaMesDe(dataIso: string): void {
    this.mes.set(paraMes(dataIso));
    this.carregarMes();
  }
}
